import { apiFetch } from '../../lib/api/client.js';
import { ApiError } from '../../lib/api/error.js';

export const REFERRAL_QUERY_KEYS = {
  summary: ['referral', 'summary'] as const,
  friends: ['referral', 'friends'] as const,
  claims: ['referral', 'claims'] as const,
};

/** When the agent's cadence lets a member take their referral income (#342). */
export type ReferralClaimMode = 'realtime' | 'daily' | 'weekly' | 'monthly';

export interface ReferralSummary {
  /**
   * Minor units the member may take RIGHT NOW — already net of
   * `expiringAmount`, which the server deducts before answering so the screen
   * cannot advertise money the next tap would destroy.
   */
  claimable: string;
  accrued: string;
  claimed: string;
  lossShare: string;
  turnoverShare: string;
  ftdBonus: string;
  subLineShare: string;
  /**
   * How much of each source's lifetime figure is still claimable (#97).
   *
   * Optional on the TYPE for the same reason the cadence block is: a reply from
   * a service that predates the split has no such fields, and the screen must
   * render zeros rather than blanks.
   *
   * DERIVED, not stored. The four are allocated from the lifetime figures in
   * proportion to `claimable` (largest-remainder, so they sum to it exactly) —
   * no per-source claimed total exists to read. Treat one of them as an exact
   * account of a single source and you will be wrong; treat the four together
   * as a breakdown of `claimable` and you will not.
   */
  claimableLossShare?: string;
  claimableTurnoverShare?: string;
  claimableFtdBonus?: string;
  claimableSubLineShare?: string;
  directFriendCount: number;
  subFriendCount: number;
  enabled: boolean;
  /**
   * Minor units the claim path refuses under (REFERRAL-001), already clamped to
   * at least 1 by the server. Optional on the TYPE for the same reason the
   * cadence block is: a service that predates it must leave the screen exactly
   * as it was rather than disabling the button against an `undefined` floor.
   */
  minClaimAmount?: string;
  /**
   * The cadence block (#342). Optional on the TYPE, not on the wire: every
   * shipped svc-commission spreads all six, but a reply from a service that
   * predates the feature must render the screen exactly as it did before
   * rather than blanking the hero on `undefined`.
   */
  claimMode?: ReferralClaimMode;
  /** Bangkok `YYYY-MM-DD` of the next day claiming opens, or null when every day is open. */
  nextOpenDay?: string | null;
  /** Claims still allowed today, or null when the cadence sets no ceiling. */
  claimsLeftToday?: number | null;
  /**
   * ISO instant the current unclaimed balance is forfeited, or null.
   *
   * An ESTIMATE, not a promise: the not-yet-due path dates the deadline from
   * the window without re-checking the balance at the opening, so a member
   * below the claim floor may be shown a forfeit that never happens. The copy
   * that renders it says so.
   */
  forfeitAt?: string | null;
  /** True when a claim right now would forfeit part of the balance. */
  expired?: boolean;
  /** Minor units a claim right now would forfeit, or null when nothing is due. */
  expiringAmount?: string | null;
}

export interface ReferralFriends {
  direct: Array<{ memberId: string; level: number }>;
  sub: Array<{ memberId: string; level: number }>;
}

export function fetchReferralSummary(): Promise<ReferralSummary> {
  return apiFetch<ReferralSummary>('/referral/me');
}

export function fetchReferralFriends(): Promise<ReferralFriends> {
  return apiFetch<ReferralFriends>('/referral/me/friends');
}

export interface ReferralClaimResult {
  claimed: string;
  txId: string;
  /** True when the server replayed a claim an earlier call in the same minute already made. */
  replay: boolean;
  /** The claim row's status; only 'completed' means the wallet credit is confirmed. */
  status: string;
  /**
   * True when this tap had to book the forfeit of an expired part of the
   * balance before the remainder could be paid. Set by `claimReferral`, not by
   * the wire.
   */
  forfeitedFirst?: boolean;
}

/** The refusal the server answers with when a claim attempt BOOKS a forfeit. */
const FORFEIT_CODE = 'REFERRAL-009';

function postClaim(): Promise<ReferralClaimResult> {
  return apiFetch<ReferralClaimResult>('/referral/me/claim', { method: 'POST' });
}

/**
 * Claim the member's referral earnings — one tap, whatever the server needs.
 *
 * A balance with an expired part takes TWO calls: the first books the forfeit
 * and refuses with REFERRAL-009 without paying anything, and only the next one
 * pays the remainder (`claimReferralIncome` in apps/commission). Left to the
 * player that is a tap, an error toast, and a second tap to reach money they
 * were owed all along (#518) — so the second call happens here.
 *
 * Exactly one retry, and only for that code: anything else is a refusal the
 * player has to see. If the retry is itself refused — the remainder sat below
 * the claim floor, say — the ORIGINAL forfeit error is what is thrown, because
 * "your balance expired" is the true account of what just happened and
 * "nothing to claim" is not.
 */
export async function claimReferral(): Promise<ReferralClaimResult> {
  try {
    return await postClaim();
  } catch (err) {
    if (!(err instanceof ApiError) || err.code !== FORFEIT_CODE) throw err;
    try {
      return { ...(await postClaim()), forfeitedFirst: true };
    } catch {
      throw err;
    }
  }
}

/** Build the shareable referral URL from the player's referralCode. */
export function buildReferralLink(origin: string, code: string): string {
  return `${origin.replace(/\/$/, '')}/register?ref=${code}`;
}

/**
 * Where shared links should point: the storefront's primary domain when one is
 * set (#595) — the host this player is on may be one an ISP is blocking — else the
 * current origin.
 */
export function shareOrigin(primaryDomain: string | null | undefined, currentOrigin: string): string {
  return primaryDomain ? `https://${primaryDomain}` : currentOrigin;
}
